import {VList, VListHandle} from 'virtua'
import {AdherenceTableFixedPartEntry} from './adherence_table_fixed_part_entry'
import {IParticipant, IStopwatchGarminStream} from '../../shared/db'
import {Dispatch, SetStateAction, useState, useEffect} from 'react'
import {createDispatchActions} from '../../store'
import {ParticipantRealtimeDataMap} from './adherence_table'
import {RequestResult} from '../../lib'

export interface AdherenceTableParticipantColumnProps {
  projectId: string | undefined
  displayParticipants: IParticipant[]
  garminStreamTaskConfig: IStopwatchGarminStream | undefined
  displayRealtimeData?: boolean
  rowHeight: number
  sendMessage: (participantList: IParticipant[]) => void
  selectedList: IParticipant[]
  setSelectedList: Dispatch<SetStateAction<IParticipant[]>>
  participantListRef: React.RefObject<HTMLDivElement>
  vListHandler: React.RefObject<VListHandle>
}

export const AdherenceTableParticipantColumn = (props: AdherenceTableParticipantColumnProps) => {
  const {doREQUEST_PROJECT_REALTIME_DATA_FETCH} = createDispatchActions()

  const {
    projectId,
    displayParticipants,
    displayRealtimeData,
    rowHeight,
    sendMessage,
    selectedList,
    setSelectedList,
    participantListRef,
    vListHandler,
  } = props

  const [lastSeenDescending, setLastSeenDescending] = useState(true)

  const [participantRealtimeDataResult, setRequestParticipantRealtimeDataResult] = useState<RequestResult | null>(null)
  const [participantRealtimeDataMap, setParticipantRealtimeDataMap] = useState<ParticipantRealtimeDataMap | undefined>()

  useEffect(() => {
    let intervalId: NodeJS.Timer | undefined

    if (displayRealtimeData) {
      intervalId = setInterval(() => {
        doREQUEST_PROJECT_REALTIME_DATA_FETCH({
          payload: {projectId, bufferTime: 2},
          setRequestResult: setRequestParticipantRealtimeDataResult,
        })
      }, 1000)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [displayRealtimeData])

  useEffect(() => {
    if (participantRealtimeDataResult?.result.statusCode == 200) {
      const particpantRealtimeDataSetList = participantRealtimeDataResult?.result.payload
        .participantRealtimeDataSetList as {
        participantId: string
        realtimeDataSetList: {timestamp: number; dataList: unknown[]}[]
      }[]
      const participantRealtimeDataMap: ParticipantRealtimeDataMap = {}
      particpantRealtimeDataSetList.forEach((item) => {
        const lastDataList = item.realtimeDataSetList.at(-1)?.dataList
        if (lastDataList) {
          const dataSet = Object.assign({}, ...lastDataList)
          if (dataSet.bbi) {
            participantRealtimeDataMap[item.participantId] = `bbi: ${dataSet.bbi}`
          } else if (dataSet.stress) {
            participantRealtimeDataMap[item.participantId] = `stress: ${dataSet.stress}`
          } else if (dataSet.bodyBattery) {
            participantRealtimeDataMap[item.participantId] = `bodyBattery: ${dataSet.bodyBattery}`
          } else if (dataSet.bpm) {
            participantRealtimeDataMap[item.participantId] = `bpm: ${dataSet.bpm}`
          } else if (dataSet.step) {
            participantRealtimeDataMap[item.participantId] = `step: ${dataSet.step}`
          } else if (dataSet.pulseOx) {
            participantRealtimeDataMap[item.participantId] = `pulseOx: ${dataSet.pulseOx}`
          }
        }
      })
      setParticipantRealtimeDataMap(participantRealtimeDataMap)
    }
  }, [participantRealtimeDataResult])

  const columnWidth = displayRealtimeData ? '632px' : '527px'

  const ParticipantHeaderCell = (props: {index: number}) => {
    const {index} = props
    const participant = displayParticipants[index]
    const participantId = participant.id
    return (
      <AdherenceTableFixedPartEntry
        {...{
          participantId,
          index,
          displayRealtimeData,
          realtimeData: participantRealtimeDataMap?.[participant.id] ?? '',
          selectedList,
          setSelectedList,
          selectableParticipantList: displayParticipants,
          rowHeight,
          sendMessage,
        }}
      />
    )
  }

  return (
    <div
      css={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        minWidth: columnWidth,
      }}>
      {/* header of fixed part */}
      <AdherenceTableFixedPartEntry
        {...{
          tableHeader: true,
          displayRealtimeData,
          selectedList,
          setSelectedList,
          selectableParticipantList: displayParticipants,
          lastSeenDescending,
          setLastSeenDescending,
          rowHeight,
          sendMessage,
        }}
      />
      <div
        ref={participantListRef}
        css={{
          position: 'relative',
          height: '100%',
          maxHeight: '100%',
          overflow: 'hidden',
        }}>
        <VList
          style={{
            height: '100%',
            width: columnWidth,
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            overflow: 'hidden', // disable scroll action
          }}
          count={displayParticipants.length}
          itemSize={rowHeight}
          horizontal={false}
          ref={vListHandler}>
          {(index) => <ParticipantHeaderCell index={index} />}
        </VList>
      </div>
    </div>
  )
}
